// Canonical SaaS plan + trial constants (Pirouette → Studio billing).
//
// Single source of truth shared by the studio billing UI and by the tests
// that pin the operator seed / migration. The operator-console Supabase
// project (operator-supabase/) is the runtime owner of the price + trial
// data; these constants mirror it so the studio-facing app and its tests
// agree with what the operator ships. Keep this file in lockstep with
// operator-supabase/seeds/01_tiers_and_categories.sql and
// operator-supabase/supabase/migrations/0014b_trial_30_days.sql.

/**
 * Default free-trial window for a new studio, in days.
 *
 * Set at provisioning time on operator.studios.trial_ends_at (see the
 * operator migration). Raised from 7 → 30 days on 2026-05-30 to give
 * studios a full billing cycle to evaluate before the first invoice.
 */
export const STUDIO_TRIAL_DAYS = 30;

/**
 * Positioning copy shown wherever a studio sees its Pirouette billing /
 * Stripe status. Studios connect their own Stripe account; Pirouette never
 * holds their payout funds. Factual statement of the connected-account
 * model only — no competitor claims.
 */
export const STRIPE_ACCOUNT_POSITIONING = {
  heading: 'You own your Stripe account',
  body: 'Payments are processed through your connected Stripe account. Tuition and fees settle directly to your own Stripe balance and payout schedule — Pirouette never holds your money.',
} as const;

export interface StudioPlanTier {
  slug: string;
  name: string;
  monthlyCents: number;
  annualCents: number;
  isCustom: boolean;
}

/**
 * The SaaS pricing ladder, per-studio, tiered by enrolled student count.
 * Mirrors operator.plan_tiers as seeded. Custom (Enterprise) tiers carry
 * 0 cents because the price is negotiated, not listed.
 */
export const STUDIO_PLAN_TIERS: readonly StudioPlanTier[] = [
  { slug: 'boutique', name: 'Boutique', monthlyCents: 3900, annualCents: 39000, isCustom: false },
  { slug: 'studio', name: 'Studio', monthlyCents: 6900, annualCents: 69000, isCustom: false },
  { slug: 'growth', name: 'Growth', monthlyCents: 12900, annualCents: 129000, isCustom: false },
  { slug: 'scale', name: 'Scale', monthlyCents: 22900, annualCents: 229000, isCustom: false },
  { slug: 'enterprise', name: 'Enterprise', monthlyCents: 0, annualCents: 0, isCustom: true },
] as const;
